import React, { useState, useEffect, useRef } from "react";
import config from "../../config";
import toast, { Toaster } from "react-hot-toast";
import { FaTimes, FaUpload } from "react-icons/fa";
import AddProductPopup from "./AddProductPopup"; // Add this import

const PurchaseModal = ({ onClose, onPurchaseAdded }) => {
    const [formData, setFormData] = useState({
        date: "",
        vendor: "",
        invoiceNumber: "",
        notes: "",
        invoiceFile: null,
    });
    const [isAddProductPopupOpen, setIsAddProductPopupOpen] = useState(false);
    const [productData, setProductData] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [showProductModal, setShowProductModal] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [vendors, setVendors] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [paymentTerms, setPaymentTerms] = useState("Due on Receipt");
    const [amountPaid, setAmountPaid] = useState("");
    const [completedDate, setCompletedDate] = useState("");
    const [productCategory, setProductCategory] = useState([]);
    const [selectedList, setSelectedList] = useState([]);
    const [hasFetchedProducts, setHasFetchedProducts] = useState(false);
    const bid = localStorage.getItem("branch_id");
    const modalRef = useRef(null);
    const fileInputRef = useRef(null);

    // Date validation regex (DD/MM/YYYY)
 const dateRegex = /^\d{4}-\d{2}-\d{2}$/;

    const handleDateChange = (e, field) => {
        const value = e.target.value;
        if (dateRegex.test(value) || value === "") {
            setErrors((prev) => ({ ...prev, [field]: "" }));
        } else {
            setErrors((prev) => ({
                ...prev,
                [field]: "Invalid date format",
            }));
        }

        if (field === "completedDate") setCompletedDate(value);
    };


    const toggleAddProductPopup = () => {
        setIsAddProductPopupOpen(!isAddProductPopupOpen);
        // Refresh products after adding a new one
        if (!isAddProductPopupOpen) {
            fetchData();
        }
    };
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        setFormData({ ...formData, invoiceFile: e.target.files[0] });
    };

    const triggerFileInput = () => {
        fileInputRef.current.click();
    };

    const fetchVendors = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${config.apiUrl}/api/swalook/vendor/expense-purchase/?branch_name=${bid}`,
                {
                    headers: {
                        Authorization: `Token ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (!response.ok) throw new Error("Failed to fetch vendors");

            const data = await response.json();
            setVendors(data.data || []);
        } catch (error) {
            console.error("Error fetching vendors:", error);
            toast.error("Failed to load vendors");
        }
    };

    const fetchData = async () => {
        try {
            const branchName = localStorage.getItem("branch_name");
            const token = localStorage.getItem("token");

            if (!branchName || !token) {
                console.error("Branch name or token is missing.");
                return;
            }

            const response = await fetch(
                `${config.apiUrl}/api/swalook/inventory/product/?branch_name=${bid}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Token ${token}`,
                    },
                }
            );

            if (!response.ok) {
                throw new Error(
                    `Failed to fetch products: ${response.statusText}`
                );
            }

            const result = await response.json();

            const categoryMap = new Map();

            result.data.forEach((product) => {
                const categoryName =
                    product.category_details?.product_category ||
                    "Uncategorized";

                if (!categoryMap.has(categoryName)) {
                    categoryMap.set(categoryName, {
                        key: categoryName,
                        value: categoryName,
                        products: [],
                    });
                }
                categoryMap.get(categoryName).products.push({
                    id: product.id,
                    name: product.product_name || "Unnamed Product",
                    price: product.product_price || 0,
                    quantity: 1,
                    staff: [],
                    note: "No notes added",
                    category: categoryName,
                });
            });

            const categories = Array.from(categoryMap.values());
            setProductCategory(categories);
            setProductData(result.data);
            setHasFetchedProducts(true);
        } catch (error) {
            console.error("Error fetching products:", error.message);
        }
    };

    const filteredproduct = productCategory.filter(
        (category) =>
            category.value?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            category.products.some((product) =>
                product.name?.toLowerCase().includes(searchQuery.toLowerCase())
            )
    );

    const toggleProductSelection = (product) => {
        setSelectedList((prevSelected) => {
            const isAlreadySelected = prevSelected.some(
                (p) => p.id === product.id
            );

            if (isAlreadySelected) {
                return prevSelected.filter((p) => p.id !== product.id);
            } else {
                return [...prevSelected, product];
            }
        });
    };

    const calculateTotal = () => {
        let total = 0;
        selectedProducts.forEach((product) => {
            if (product.isDiscount) {
                total -= Number(product.rate);
            } else if (product.isTax) {
                total += Number(product.rate);
            } else if (product.isAdjustment) {
                total += Number(product.rate); // Can be positive/negative
            } else {
                total +=
                    Number(product.quantity) *
                    Number(product.rate) *
                    (1 + Number(product.gst) / 100);
            }
        });
        return total;
    };

    const handleProductQuantityChange = (index, quantity) => {
        const updatedProducts = [...selectedProducts];
        updatedProducts[index].quantity = quantity;
        setSelectedProducts(updatedProducts);
    };

    const handleProductRateChange = (index, rate) => {
        const updatedProducts = [...selectedProducts];
        updatedProducts[index].rate = Number(rate); // ← Always keep rate a number!
        setSelectedProducts(updatedProducts);
    };

    const handleProductGSTChange = (index, gst) => {
        const updatedProducts = [...selectedProducts];
        updatedProducts[index].gst = Number(gst);
        setSelectedProducts(updatedProducts);
    };

    const handleAddDiscount = () => {
        setSelectedProducts([
            ...selectedProducts,
            {
                id: "discount",
                name: "Discount",
                quantity: 1,
                rate: 0,
                gst: 0,
                isDiscount: true,
            },
        ]);
    };

    const handleAddTax = () => {
        setSelectedProducts([
            ...selectedProducts,
            {
                id: "tax",
                name: "Tax (TDS/TCS)",
                quantity: 1,
                rate: 0,
                gst: 0,
                isTax: true,
            },
        ]);
    };

    const handleAddAdjustment = () => {
        setSelectedProducts([
            ...selectedProducts,
            {
                id: "adjustment",
                name: "Adjustment",
                quantity: 1,
                rate: 0,
                gst: 0,
                isAdjustment: true,
            },
        ]);
    };

    const handleRemoveProduct = (index) => {
        const updatedProducts = [...selectedProducts];
        updatedProducts.splice(index, 1);
        setSelectedProducts(updatedProducts);
    };

 const formatDateForAPI = (dateString) => {
    if (!dateString) return null;
    return dateString; // Already in YYYY-MM-DD format
};

    const finalizeSelection = () => {
        const newProducts = selectedList.map((product) => ({
            id: product.id,
            name: product.name,
            quantity: 1,
            rate: product.price,
            gst: 0,
        }));
        setSelectedProducts(newProducts);
        setShowProductModal(false);
    };
    const handleSubmit = async () => {
        setIsLoading(true);

        try {
            const token = localStorage.getItem("token");
            const formDataToSend = new FormData();

            // Validate required fields
            if (!formData.vendor) {
                throw new Error("Vendor is required");
            }

            // Convert dates to proper format
            const currentDate = new Date().toISOString().split("T")[0];
            const formattedCompletedDate = completedDate
                ? formatDateForAPI(completedDate)
                : "";

            // Required fields from error response
            formDataToSend.append("vendor", formData.vendor);
            if (formData.invoiceFile) {
                formDataToSend.append("image_1", formData.invoiceFile);
            }
            // Other fields
            formDataToSend.append("uploaded_at", currentDate);
            formDataToSend.append("completed_date", formattedCompletedDate);
            formDataToSend.append("invoice_id", formData.invoiceNumber || "");
            formDataToSend.append(
                "payment_term",
                paymentTerms || "Due on Receipt"
            );
            formDataToSend.append("amount_paid", amountPaid || "0");
            formDataToSend.append("note", formData.notes || "");
            formDataToSend.append("branch_id", bid);

            // Prepare products data as JSON
            const productsData = selectedProducts
                .filter((p) => !p.isDiscount && !p.isTax && !p.isAdjustment)
                .map((product) => ({
                    product_id: product.id,
                    quantity: product.quantity,
                    rate: product.rate,
                    gst: product.gst,
                }));

            formDataToSend.append("products", JSON.stringify(productsData));

            // Add discounts, taxes, adjustments
            const discount = selectedProducts.find((p) => p.isDiscount);
            const tax = selectedProducts.find((p) => p.isTax);
            const adjustment = selectedProducts.find((p) => p.isAdjustment);

            if (discount) {
                formDataToSend.append("discount", discount.rate);
            } else {
                formDataToSend.append("discount", "0");
            }

            if (tax) {
                formDataToSend.append("tax", tax.rate);
            } else {
                formDataToSend.append("tax", "0");
            }

            if (adjustment) {
                formDataToSend.append("adjustment", adjustment.rate);
            } else {
                formDataToSend.append("adjustment", "0");
            }

            const response = await fetch(
                `${config.apiUrl}/api/swalook/vendor-expense-purchase/?branch_name=${bid}`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Token ${token}`,
                    },
                    body: formDataToSend,
                }
            );

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.message || "Failed to create purchase");
            }

            toast.success("Purchase created successfully!");
            onPurchaseAdded(result.data);
            onClose();
        } catch (error) {
            console.error("Error creating purchase:", error);
            toast.error(error.message || "Failed to create purchase");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchVendors();
    }, []);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Toaster />
            <div className="bg-white rounded-2xl w-full max-w-5xl max-h-screen m-4 overflow-y-auto p-4">
                <div className="sticky top-0 bg-white p-6 border-b flex justify-between items-center">
                    <h2 className="text-2xl font-bold text-gray-800">
                        New Purchase Entry
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-red-500 hover:text-red-700 text-3xl font-bold"
                    >
                        &times;
                    </button>
                </div>

                {/* Purchase Details Section */}
                <div className="p-6">
                    <h3 className="text-lg font-semibold mb-4">
                        Purchase Details:
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Date*
                            </label>
                            <input
                                type="date"
                                name="date"
                                value={formData.date}
                                onChange={handleChange}
                                className="w-full p-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Invoice ID
                            </label>
                            <input
                                type="text"
                                name="invoiceNumber"
                                value={formData.invoiceNumber}
                                onChange={handleChange}
                                placeholder="Invoice Number"
                                className="w-full p-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Vendor*
                            </label>
                            <select
                                name="vendor"
                                value={formData.vendor}
                                onChange={handleChange}
                                className="w-full p-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                                required
                            >
                                <option value="">Select Vendor</option>
                                {vendors.map((vendor) => (
                                    <option key={vendor.id} value={vendor.id}>
                                        {vendor.vendor_name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        {/* Inventory Details Section */}
                        <div className="flex flex-col">
                            <h3 className="text-lg font-semibold mb-4">
                                Inventory Details
                            </h3>
                            <button
                                type="button"
                                className="px-6 py-2 border-2 border-blue-500 text-blue-500 font-semibold rounded-full hover:bg-blue-500 hover:text-white transition duration-300 mb-4"
                                onClick={async () => {
                                    await fetchData();
                                    setShowProductModal(true);
                                }}
                            >
                                Add Products
                            </button>
                        </div>
                        {/* Upload Invoice Button */}
                        <div className="flex flex-col">
                            <h3 className="text-lg font-semibold mb-4">
                                Upload Invoice
                            </h3>

                            <div className=" mb-6 w-full">
                                <button
                                    type="button"
                                    onClick={triggerFileInput}
                                    className="flex items-center gap-2 px-4 py-2 border border-blue-500 font-semibold rounded-full text-blue-500 hover:bg-blue-500 hover:text-white transition duration-300"
                                >
                                    <FaUpload />
                                    Upload File
                                </button>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleFileChange}
                                    className="hidden"
                                    accept=".pdf,.jpg,.jpeg,.png"
                                />
                                {formData.invoiceFile && (
                                    <p className="mt-2 text-sm text-gray-600">
                                        {formData.invoiceFile.name}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    {selectedProducts.length > 0 && (
                        <div className="overflow-x-auto mb-6">
                            <table className="w-full border border-gray-200 rounded-lg">
                                <thead className="bg-gray-100">
                                    <tr>
                                        <th className="border border-gray-200 px-4 py-2">
                                            Product Name
                                        </th>
                                        <th className="border border-gray-200 px-4 py-2">
                                            Quantity
                                        </th>
                                        <th className="border border-gray-200 px-4 py-2">
                                            Rate (₹)
                                        </th>
                                        <th className="border border-gray-200 px-4 py-2">
                                            GST (%)
                                        </th>
                                        <th className="border border-gray-200 px-4 py-2">
                                            Total (₹)
                                        </th>
                                        <th className="border border-gray-200 px-4 py-2">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {selectedProducts.map((product, index) => (
                                        <tr key={index}>
                                            <td className="border border-gray-200 px-4 py-2">
                                                {product.name}
                                            </td>
                                            <td className="border border-gray-200 px-4 py-2">
                                                {product.isDiscount ||
                                                product.isTax ||
                                                product.isAdjustment ? (
                                                    "N/A"
                                                ) : (
                                                    <input
                                                        type="number"
                                                        className="w-20 p-1 border rounded text-center"
                                                        value={
                                                            product.quantity ===
                                                            0
                                                                ? ""
                                                                : Number(
                                                                      product.quantity
                                                                  )
                                                        }
                                                        onChange={(e) =>
                                                            handleProductQuantityChange(
                                                                index,
                                                                e.target.value
                                                            )
                                                        }
                                                        min="1"
                                                    />
                                                )}
                                            </td>
                                            <td className="border border-gray-200 px-4 py-2">
                                                <input
                                                    type="number"
                                                    className="w-24 p-1 border rounded text-center"
                                                    value={
                                                        product.rate === 0
                                                            ? ""
                                                            : Number(
                                                                  product.rate
                                                              )
                                                    }
                                                    onChange={(e) =>
                                                        handleProductRateChange(
                                                            index,
                                                            e.target.value
                                                        )
                                                    }
                                                    min="0"
                                                    step="0.01"
                                                />
                                            </td>
                                            <td className="border border-gray-200 px-4 py-2">
                                                {product.isDiscount ||
                                                product.isTax ||
                                                product.isAdjustment ? (
                                                    "N/A"
                                                ) : (
                                                    <select
                                                        className="w-20 p-1 border rounded"
                                                        value={
                                                            product.gst === 0
                                                                ? ""
                                                                : Number(
                                                                      product.gst
                                                                  )
                                                        }
                                                        onChange={(e) =>
                                                            handleProductGSTChange(
                                                                index,
                                                                e.target.value
                                                            )
                                                        }
                                                    >
                                                        <option value="0">
                                                            0%
                                                        </option>
                                                        <option value="5">
                                                            5%
                                                        </option>
                                                        <option value="12">
                                                            12%
                                                        </option>
                                                        <option value="18">
                                                            18%
                                                        </option>
                                                        <option value="28">
                                                            28%
                                                        </option>
                                                    </select>
                                                )}
                                            </td>
                                            <td className="border border-gray-200 px-4 py-2 text-right">
                                                {product.isDiscount
                                                    ? `-₹${Number(
                                                          product.rate
                                                      ).toFixed(2)}`
                                                    : product.isTax
                                                    ? `+₹${Number(
                                                          product.rate
                                                      ).toFixed(2)}`
                                                    : product.isAdjustment
                                                    ? `${
                                                          Number(product.rate) <
                                                          0
                                                              ? "-"
                                                              : "+"
                                                      }₹${Math.abs(
                                                          Number(product.rate)
                                                      ).toFixed(2)}`
                                                    : `₹${(
                                                          Number(
                                                              product.quantity
                                                          ) *
                                                          Number(product.rate) *
                                                          (1 +
                                                              Number(
                                                                  product.gst
                                                              ) /
                                                                  100)
                                                      ).toFixed(2)}`}
                                            </td>
                                            <td className="border border-gray-200 px-4 py-2 text-center">
                                                <button
                                                    onClick={() =>
                                                        handleRemoveProduct(
                                                            index
                                                        )
                                                    }
                                                    className="text-red-500 hover:text-red-700"
                                                >
                                                    Remove
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    <tr className="bg-gray-50">
                                        <td
                                            colSpan="4"
                                            className="border border-gray-200 px-4 py-2 font-semibold text-right"
                                        >
                                            Grand Total:
                                        </td>
                                        <td className="border border-gray-200 px-4 py-2 font-semibold text-right">
                                            ₹{calculateTotal().toFixed(2)}
                                        </td>
                                        <td className="border border-gray-200 px-4 py-2"></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Additional Options */}
                    <div className="flex gap-2 mb-6">
                        <button
                            type="button"
                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-full hover:bg-gray-300"
                            onClick={handleAddDiscount}
                        >
                            Add Discount
                        </button>
                        <button
                            type="button"
                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-full hover:bg-gray-300"
                            onClick={handleAddTax}
                        >
                            Add Tax (TDS/TCS)
                        </button>
                        <button
                            type="button"
                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-full hover:bg-gray-300"
                            onClick={handleAddAdjustment}
                        >
                            Add Adjustment
                        </button>
                    </div>

                    {/* Payment Details Section */}
                    <h3 className="text-lg font-semibold mb-4">
                        Payment Details:
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Payment Terms
                            </label>
                            <select
                                value={paymentTerms}
                                onChange={(e) =>
                                    setPaymentTerms(e.target.value)
                                }
                                className="w-full p-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="Due on Receipt">
                                    Due on Receipt
                                </option>
                                <option value="7 Days">7 Days</option>
                                <option value="15 Days">15 Days</option>
                                <option value="30 Days">30 Days</option>
                                <option value="60 Days">60 Days</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Amount Paid (₹)
                            </label>
                            <input
                                type="number"
                                placeholder="Amount Paid"
                                value={amountPaid}
                                onChange={(e) => setAmountPaid(e.target.value)}
                                className="w-full p-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Completed Date (DD/MM/YYYY)
                            </label>
                            <input
                                type="date"
                                placeholder="Completed Date"
                                value={completedDate}
                                onChange={(e) =>
                                    handleDateChange(e, "completedDate")
                                }
                                className={`w-full p-3 border rounded-full focus:outline-none focus:ring-2 ${
                                    errors.completedDate
                                        ? "border-red-500"
                                        : "border-gray-300 focus:ring-blue-500"
                                }`}
                            />
                            {errors.completedDate && (
                                <p className="text-red-500 text-sm mt-1">
                                    {errors.completedDate}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Notes Section */}
                    <div className="mb-6">
                        <h3 className="text-lg font-semibold mb-2">Notes:</h3>
                        <textarea
                            placeholder="Additional Notes"
                            name="notes"
                            value={formData.notes}
                            onChange={handleChange}
                            className="w-full p-3 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 h-24"
                        ></textarea>
                    </div>

                    {/* Submit Button */}
                    <div className="flex justify-center gap-4">
                        <button
                            onClick={handleSubmit}
                            disabled={isLoading}
                            className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-8 rounded-full transition duration-300 disabled:opacity-50"
                        >
                            {isLoading ? "Processing..." : "Add Purchase"}
                        </button>
                        <button
                            onClick={onClose}
                            className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-3 px-8 rounded-full transition duration-300"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>

            {/* Product Selection Modal */}
            {showProductModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                    <div
                        ref={modalRef}
                        className="bg-white rounded-2xl p-6 w-4/5 max-w-5xl overflow-y-auto max-h-[90vh]"
                    >
                        <div className="flex justify-between items-center mb-4">
                            <span></span>
                            <FaTimes
                                size={24}
                                className="text-red-500 cursor-pointer hover:text-red-700"
                                aria-label="Close Modal"
                                onClick={() => setShowProductModal(false)}
                            />
                        </div>

                        <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
                            <h3 className="text-2xl font-bold">
                                Select Products
                            </h3>
                            <div className="flex gap-2 items-center mb-4">
                                <input
                                    type="text"
                                    placeholder="Search products or categories..."
                                    className="border border-gray-300 rounded-full px-6 py-2 w-full font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                    value={searchQuery}
                                    onChange={(e) =>
                                        setSearchQuery(e.target.value)
                                    }
                                />
                                <button
                                    type="button"
                                    className="whitespace-nowrap px-6 py-2 border-2 border-green-500 text-green-500 font-medium rounded-full hover:bg-green-500 hover:text-white transition duration-300 text-sm"
                                    onClick={toggleAddProductPopup}
                                >
                                    Add New Product
                                </button>
                            </div>

                            {isAddProductPopupOpen && (
                                <AddProductPopup
                                    onClose={toggleAddProductPopup}
                                    onProductAdded={() => {
                                        toggleAddProductPopup();
                                        fetchData();
                                    }}
                                />
                            )}
                        </div>

                        {filteredproduct.length === 0 ? (
                            <p className="text-center text-gray-500">
                                No products available
                            </p>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {filteredproduct.map((category) => (
                                    <div
                                        key={category.key}
                                        className="bg-gray-100 p-4 rounded-lg border"
                                    >
                                        <h4 className="text-lg font-semibold mb-4">
                                            {category.value}
                                        </h4>
                                        <ul className="space-y-2">
                                            {category.products.map(
                                                (product) => (
                                                    <li
                                                        key={product.id}
                                                        className="flex items-center justify-between"
                                                    >
                                                        <label className="flex flex-row items-center gap-3 cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedList.some(
                                                                    (p) =>
                                                                        p.id ===
                                                                        product.id
                                                                )}
                                                                onChange={() =>
                                                                    toggleProductSelection(
                                                                        product
                                                                    )
                                                                }
                                                                className="h-4 w-4 rounded-full"
                                                            />
                                                            <p className="font-medium">
                                                                {product.name}
                                                            </p>
                                                        </label>
                                                        <p className="text-base font-semibold text-gray-700">
                                                            ₹{product.price}
                                                        </p>
                                                    </li>
                                                )
                                            )}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="sticky bottom-0 left-0 right-0 bg-white p-4 border-t mt-4">
                            <div className="flex justify-between items-center">
                                <p className="text-lg font-semibold">
                                    Selected:{" "}
                                    {selectedList
                                        .map((p) => p.name)
                                        .join(", ") || "None"}
                                </p>

                                <button
                                    type="button"
                                    className="bg-blue-500 hover:bg-blue-600 text-white font-semibold px-6 py-2 rounded-full transition duration-300"
                                    onClick={finalizeSelection}
                                >
                                    Add Product
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PurchaseModal;
